import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Use environment variable or fallback to preview URL for testing
const BASE_URL = process.env.REACT_APP_SITE_URL || 
                 (window.location.hostname === 'localhost' 
                   ? 'https://axio-seo.preview.emergentagent.com'
                   : window.location.origin);

// SEO configuration for each page
const seoConfig = {
  '/': {
    title: 'PriceStrike | AI Pricing Intelligence for SMBs | AxioSera',
    description: 'Stop guessing your prices. PriceStrike is the AI-powered pricing intelligence platform that helps SMBs simulate, protect, and optimize pricing decisions with confidence.',
    image: '/og-image-home.png',
    canonical: '/'
  },
  '/home': {
    title: 'Platform Overview | PriceStrike by AxioSera',
    description: 'Discover how PriceStrike helps SMBs make confident pricing decisions with AI-powered simulation, guardrails, and competitive intelligence.',
    image: '/og-image-home.png',
    canonical: '/home'
  },
  '/product': {
    title: 'Product Features | PriceStrike - AI Pricing Intelligence',
    description: 'Explore PriceStrike features: price simulation, margin optimization, competitive intelligence, and AI-powered recommendations for SMBs.',
    image: '/og-image-product.png',
    canonical: '/product'
  },
  '/pricing': {
    title: 'Pricing Plans | PriceStrike - Start Free, Scale with Confidence',
    description: 'Simple, transparent pricing for businesses of all sizes. Start with a free trial and upgrade as you grow. No hidden fees.',
    image: '/og-image-pricing.png',
    canonical: '/pricing'
  },
  '/pricing-check': {
    title: 'Free Pricing Health Check | PriceStrike',
    description: 'Discover hidden margin opportunities in your pricing. Upload your data and get instant insights on pricing risks and opportunities.',
    image: '/og-image-pricing-check.png',
    canonical: '/pricing-check'
  },
  '/industries': {
    title: 'Industries We Serve | PriceStrike by AxioSera',
    description: 'PriceStrike serves e-commerce, retail, wholesale, SaaS, and more. See how AI pricing intelligence works for your industry.',
    image: '/og-image-default.png',
    canonical: '/industries'
  },
  '/case-studies': {
    title: 'Customer Success Stories | PriceStrike',
    description: 'See how businesses like yours improved margins, reduced pricing errors, and gained confidence with PriceStrike.',
    image: '/og-image-default.png',
    canonical: '/case-studies'
  },
  '/about': {
    title: 'About AxioSera | The Company Behind PriceStrike',
    description: 'AxioSera builds intelligent business tools that help SMBs make confident decisions. Learn about our mission and team.',
    image: '/og-image-default.png',
    canonical: '/about'
  },
  '/contact': {
    title: 'Contact Us | PriceStrike by AxioSera',
    description: 'Get in touch with the PriceStrike team. We\'d love to hear from you and discuss how we can help your business.',
    image: '/og-image-contact.png',
    canonical: '/contact'
  },
  '/strategy': {
    title: 'SMB Pricing Playbook | Understanding Pricing Risk',
    description: 'A practical guide for SMB owners on pricing risk, use cases, and how PriceStrike helps you make confident pricing decisions.',
    image: '/og-image-strategy.png',
    canonical: '/strategy'
  },
  '/ted-talk': {
    title: 'Why Businesses Are Pricing Blind | TED-Style Talk',
    description: 'A thought-provoking presentation on why pricing is broken and how AI changes that. Watch the full TED-style talk.',
    image: '/og-image-ted-talk.png',
    canonical: '/ted-talk'
  },
  '/shopify-talk': {
    title: 'Shopify Merchants: Stop Guessing Your Prices',
    description: 'A practical TED-style talk for Shopify merchants on pricing challenges and how to stop guessing.',
    image: '/og-image-shopify-talk.png',
    canonical: '/shopify-talk'
  },
  '/coming-soon': {
    title: 'Coming Soon | PriceStrike Launch',
    description: 'Be the first to know when PriceStrike launches. Sign up for early access and exclusive updates.',
    image: '/og-image-default.png',
    canonical: '/coming-soon'
  },
  '/partners': {
    title: 'Partner Program | PriceStrike by AxioSera',
    description: 'Join the PriceStrike partner program. Help your clients optimize pricing while growing your business.',
    image: '/og-image-default.png',
    canonical: '/partners'
  },
  '/insights': {
    title: 'Pricing Insights & Blog | PriceStrike',
    description: 'Expert insights on pricing strategy, AI in business, and practical tips for SMB owners.',
    image: '/og-image-default.png',
    canonical: '/insights'
  },
  '/roi-calculator': {
    title: 'ROI Calculator | See Your Potential Savings',
    description: 'Calculate how much you could save with better pricing. Our ROI calculator shows your potential margin improvement.',
    image: '/og-image-default.png',
    canonical: '/roi-calculator'
  }
};

// Default SEO config
const defaultSeo = {
  title: 'PriceStrike | AI Pricing Intelligence for SMBs | AxioSera',
  description: 'Stop guessing your prices. PriceStrike is the AI-powered pricing intelligence platform that helps SMBs simulate, protect, and optimize pricing decisions.',
  image: '/og-image-default.png',
  canonical: '/'
};

const SEOHead = () => {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    const seo = seoConfig[path] || defaultSeo;
    
    // Update document title
    document.title = seo.title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seo.description);
    }
    
    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${BASE_URL}${seo.canonical}`);
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    
    if (ogTitle) ogTitle.setAttribute('content', seo.title);
    if (ogDescription) ogDescription.setAttribute('content', seo.description);
    if (ogImage) ogImage.setAttribute('content', `${BASE_URL}${seo.image}`);
    if (ogUrl) ogUrl.setAttribute('content', `${BASE_URL}${seo.canonical}`);
    
    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    
    if (twitterTitle) twitterTitle.setAttribute('content', seo.title);
    if (twitterDescription) twitterDescription.setAttribute('content', seo.description);
    if (twitterImage) twitterImage.setAttribute('content', `${BASE_URL}${seo.image}`);
    if (twitterUrl) twitterUrl.setAttribute('content', `${BASE_URL}${seo.canonical}`);
    
  }, [location.pathname]);
  
  return null;
};

export default SEOHead;
